import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import type { EtfDetail } from "../../../api/services/etf.type";
import {
  formatAum,
  formatCount,
  formatExpenseRatio,
  formatNaiveDate,
} from "../EtfScreener/etfFormat";
import styles from "./etfDetail.module.css";

const DASH = "—";

/**
 * Identity and basket facts. Venue (where it trades, from the ticker suffix)
 * and domicile (listing_country) are separate rows under names that say
 * which is which (handover §12.1). A fund has no financial ratios (§12.2).
 */
export const EtfFacts = ({ fund }: { fund: EtfDetail }) => {
  const { t } = useTranslation();
  const p = "pages.etfDetail.facts.";
  const domicileKey = `pages.etfScreener.filters.domiciles.${fund.listingCountry}`;
  const domicile = fund.listingCountry
    ? t(domicileKey, { defaultValue: fund.listingCountry })
    : DASH;

  const rows: [string, string][] = [
    [t(`${p}issuer`), fund.issuer ?? DASH],
    [t(`${p}venue`), fund.venue ? t(`pages.etfScreener.venueNames.${fund.venue}`) : DASH],
    [t(`${p}domicile`), domicile],
    [t(`${p}currency`), fund.reportingCurrency ?? DASH],
    [t(`${p}expense`), formatExpenseRatio(fund.expenseRatio)],
    [t(`${p}aum`), formatAum(fund.aum, fund.reportingCurrency)],
    [
      t(`${p}holdings`),
      fund.holdingsTotal === null
        ? DASH
        : t(`${p}holdingsValue`, {
            total: formatCount(fund.holdingsTotal),
            screened: formatCount(fund.holdingsScreened),
          }),
    ],
    [t(`${p}basketAsOf`), formatNaiveDate(fund.holdingsAsOf)],
    [t(`${p}basketFetched`), formatNaiveDate(fund.holdingsFetchedAt)],
    [t(`${p}lastUpdated`), formatNaiveDate(fund.lastUpdated)],
  ];

  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>{t(`${p}title`)}</h2>
      <dl className={styles.facts}>
        {rows.map(([label, value]) => (
          <Fragment key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </Fragment>
        ))}
      </dl>
      <p className={styles.note}>{t("pages.etfScreener.filters.notes.venue")}</p>
    </section>
  );
};
